import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Button,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import { tenantApiFetch } from './tenantApi';
import { TenantCan } from '../components/tenant/TenantCan';

const STATUSES = ['draft', 'published', 'archived'];
const VIS = ['public', 'authenticated', 'tenant'];

function statusColor(s) {
  if (s === 'published') return 'success';
  if (s === 'archived') return 'default';
  return 'warning';
}

export default function TenantKnowledgeArticlesListPage() {
  const navigate = useNavigate();
  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [appliedStatus, setAppliedStatus] = useState('');
  const [catInput, setCatInput] = useState('');
  const [appliedCat, setAppliedCat] = useState('');
  const [visInput, setVisInput] = useState('');
  const [appliedVis, setAppliedVis] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page: String(page + 1), pageSize: String(pageSize) });
    if (appliedQ) params.set('q', appliedQ);
    if (appliedStatus) params.set('status', appliedStatus);
    if (appliedCat) params.set('category', appliedCat);
    if (appliedVis) params.set('visibility', appliedVis);
    try {
      const res = await tenantApiFetch(`/api/tenant/knowledge-base/articles?${params}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      setData(json);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedQ, appliedStatus, appliedCat, appliedVis]);

  useEffect(() => {
    load();
  }, [load]);

  const apply = () => {
    setPage(0);
    setAppliedQ(qInput.trim());
    setAppliedStatus(statusInput);
    setAppliedCat(catInput.trim());
    setAppliedVis(visInput);
  };

  const rows = data?.articles || [];
  const total = data?.pagination?.total ?? 0;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <TenantCan permission="tenant.kb.manage">
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/tenant/knowledge-base/new')}>
            New article
          </Button>
        </TenantCan>
      </Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            label="Search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && apply()}
          />
          <TextField
            size="small"
            label="Status"
            select
            value={statusInput}
            onChange={(e) => setStatusInput(e.target.value)}
            sx={{ minWidth: 140 }}
            SelectProps={{ native: true }}
          >
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Category"
            value={catInput}
            onChange={(e) => setCatInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && apply()}
          />
          <TextField size="small" label="Visibility" select value={visInput} onChange={(e) => setVisInput(e.target.value)} sx={{ minWidth: 160 }} SelectProps={{ native: true }}>
            <option value="">All</option>
            {VIS.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </TextField>
          <Button variant="outlined" onClick={apply}>
            Apply
          </Button>
        </Box>
      </Paper>
      <Paper sx={{ overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Visibility</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.title}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.slug}</TableCell>
                    <TableCell>{row.category}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status} color={statusColor(row.status)} variant="outlined" />
                    </TableCell>
                    <TableCell>{row.visibility}</TableCell>
                    <TableCell>{row.updated_at ? String(row.updated_at).slice(0, 10) : '—'}</TableCell>
                    <TableCell align="right">
                      <IconButton component={Link} to={`/tenant/knowledge-base/${row.id}`} size="small" aria-label="View">
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <TenantCan permission="tenant.kb.manage">
                        <IconButton component={Link} to={`/tenant/knowledge-base/${row.id}/edit`} size="small" aria-label="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TenantCan>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {rows.length === 0 && (
              <Box sx={{ p: 3 }}>
                <Typography color="text.secondary">No articles yet.</Typography>
              </Box>
            )}
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
